import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type ModalProps = {
  children: ReactNode;
  onClose?: () => void;
  wide?: boolean;
  label?: string;
  closeLabel?: string;
  className?: string;
};

export default function Modal({
  children,
  onClose,
  wide = false,
  label = 'Dialog',
  closeLabel = 'Close',
  className = '',
}: ModalProps) {
  const [closing, setClosing] = useState(false);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const closingRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const close = useCallback(() => {
    if (!onCloseRef.current || closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
  }, []);

  useEffect(() => {
    const backdrop = backdropRef.current;
    const modal = modalRef.current;
    if (!backdrop || !modal) return undefined;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const backgroundModals = Array.from(
      document.querySelectorAll<HTMLElement>('.modal-backdrop'),
    )
      .filter((element) => element !== backdrop)
      .map((element) => ({
        element,
        ariaHidden: element.getAttribute('aria-hidden'),
        inert: element.hasAttribute('inert'),
      }));

    backgroundModals.forEach(({ element }) => {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    });
    modal.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      const backdrops = document.querySelectorAll('.modal-backdrop');
      if (backdrops[backdrops.length - 1] !== backdrop) return;

      if (event.key === 'Escape' && onCloseRef.current) {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.getAttribute('aria-hidden') !== 'true');

      if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === modal || active === first || !modal.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      backgroundModals.forEach(({ element, ariaHidden, inert }) => {
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
        if (!inert) element.removeAttribute('inert');
      });
      previouslyFocused?.focus();
    };
  }, [close]);

  return createPortal(
    <div
      ref={backdropRef}
      className={`modal-backdrop ${closing ? 'is-closing' : ''}`}
      onMouseDown={(event: any) => event.target === event.currentTarget && close()}
      onAnimationEnd={(event: any) => {
        if (closing && event.target === event.currentTarget) onCloseRef.current?.();
      }}
    >
      <section
        ref={modalRef}
        className={`modal ${wide ? 'wide' : ''} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {onClose && (
          <button
            type="button"
            className="icon-btn modal-x"
            aria-label={`${closeLabel}: ${label}`}
            onClick={close}
          >
            <X size={20} />
          </button>
        )}
        {children}
      </section>
    </div>,
    document.body,
  );
}
